import { useQuery } from "@tanstack/react-query";
import { useEffect } from "react";
import { getAccountPermissions, getMyPermissions } from "src/queries/services/permissions";
import useAppStore from "src/stores/appStore";
import { setPermissionsSelector } from "src/stores/selectors/appSelectors";
import { HYRO_ADMIN_PANEL_ACCOUNT_ID } from "@consts";

const PERMISSIONS_STALE_TIME_MS = 5 * 60 * 1000;

export const useMyAccountPermissions = (accountId) => useQuery({
  queryKey: ["myPermissions", accountId],
  // The admin panel is a pseudo-account with no server-side membership — it runs on
  // the account-independent baseline set.
  queryFn: () => (accountId === HYRO_ADMIN_PANEL_ACCOUNT_ID ? getMyPermissions() : getAccountPermissions(accountId)),
  enabled: Boolean(accountId),
  staleTime: PERMISSIONS_STALE_TIME_MS
});

// Token claims describe the user and their own tenant, so they may seed that tenant
// and the admin-panel pseudo-account (whose gates are user-level permissions). For any
// other account a failed server fetch degrades to an empty set — hiding UI beats
// gating it with another tenant's permissions (the server authorizes every request
// regardless).
const claimsDescribeAccount = (accountId, tenantId) =>
  tenantId === accountId || accountId === HYRO_ADMIN_PANEL_ACCOUNT_ID;

const tokenClaimPermissions = (accountId) => {
  const { userData } = useAppStore.getState();
  if (!claimsDescribeAccount(accountId, userData?.tenantId)) return [];
  return userData?.permissions?.map((permission) => permission.key) ?? [];
};

export const usePermissionsSync = (accountId) => {
  const { data, isError } = useMyAccountPermissions(accountId);
  const stored = useAppStore(state => state.userPermissions);
  const tenantId = useAppStore(state => state.userData?.tenantId);
  const setPermissions = useAppStore(setPermissionsSelector);

  const serverPermissions = data?.permissions ?? null;
  const inSync = stored?.accountId === accountId && stored?.fromServer === Boolean(serverPermissions);

  useEffect(() => {
    if (!accountId || inSync) return;
    setPermissions({
      permissions: serverPermissions ?? tokenClaimPermissions(accountId),
      accountId,
      fromServer: Boolean(serverPermissions)
    });
  }, [serverPermissions, inSync, accountId, setPermissions]);

  const authoritative = Boolean(stored?.fromServer) || isError || claimsDescribeAccount(accountId, tenantId);
  return { permissionsReady: stored?.accountId === accountId && authoritative };
};
